import { useCallback, useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useConfig, getConfigValue } from '../context/ConfigContext';

/* Sentinel for "key not found". Passing `undefined` to getConfigValue would
   trigger its `defaultValue = ''` default parameter, so missing keys came
   back as '' and the caller's fallback never applied. */
const MISSING = Symbol('missing');

/**
 * Translation hook that combines LanguageContext and ConfigContext
 * Provides easy access to translated strings and shared config
 */
export function useTranslation() {
    const { language } = useLanguage();
    const { translations, shared, meta, loading } = useConfig();

    // Get the translation object for the current language
    const currentTranslation = useMemo(() => {
        const trans = translations[language] || translations[meta?.defaultLanguage] || translations.nl || {};
        return trans;
    }, [translations, language, meta?.defaultLanguage]);

    /**
     * Get a translated string by path
     * @param {string} path - Dot-notation path (e.g., 'company.name', 'dialogs.locationRequest.title')
     * @param {string} defaultValue - Fallback value if not found
     * @returns {any} The translated value
     */
    const t = useCallback((path, defaultValue = '') => {
        // Get translation directly from translations object using current language
        const langTranslation = translations[language] || translations[meta?.defaultLanguage] || translations.nl || {};
        let value = getConfigValue(langTranslation, path, MISSING);

        // If not found and we have a default language, try that
        if (value === MISSING && meta?.defaultLanguage && language !== meta.defaultLanguage) {
            const defaultTranslation = translations[meta.defaultLanguage] || {};
            value = getConfigValue(defaultTranslation, path, MISSING);
        }

        return value !== MISSING ? value : defaultValue;
    }, [translations, language, meta?.defaultLanguage]);

    /**
     * Get a shared config value (language-independent)
     * @param {string} path - Dot-notation path (e.g., 'branding.primaryColor', 'images.logo')
     * @param {any} defaultValue - Fallback value if not found
     * @returns {any} The config value
     */
    const getShared = useCallback((path, defaultValue = '') => {
        return getConfigValue(shared, path, defaultValue);
    }, [shared]);

    /**
     * Get branding config
     */
    const branding = useMemo(() => shared?.branding || {}, [shared]);

    /**
     * Get images config
     */
    const images = useMemo(() => shared?.images || {}, [shared]);

    /**
     * Get links config
     */
    const links = useMemo(() => shared?.links || {}, [shared]);

    return useMemo(() => ({
        // Translation function
        t,
        // Current language code
        language,
        // Loading state
        loading,
        // Current language's full translation object
        translation: currentTranslation,
        // Shared config accessor
        getShared,
        // Direct access to shared config sections
        branding,
        images,
        links,
        // Full shared config
        shared
    }), [t, language, loading, currentTranslation, getShared, branding, images, links, shared]);
}

export default useTranslation;
